import { externalUrls } from "@/constants/url";
import { useIsMobile } from "@/hooks/use-mobile";
import { NewTrackingEvent } from "@/lib/track/track-event";
import { trackNewEvent } from "@/lib/track/track-event";
import { cn } from "@/lib/utils";
import { useToastMessage } from "@backpac/monica";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetTrigger,
  Button,
  Dropdown,
  DropdownContent,
  DropdownTrigger,
  Icon,
  Thumbnail,
  ThumbnailFallback,
  ThumbnailImage,
  Typography,
  useModal,
} from "@backpac/monica";
import { type HTMLAttributes, useState } from "react";
import { renderSVG as renderQRAsSVG } from "uqr";

interface UseMusicButtonProps {
  isrc: string;
  title: string;
  youtubeVideoId: string;
  thumbnailImageUrl: string;
  className?: string;
}

export function UseMusicButton(props: UseMusicButtonProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <UseMusicButton.BottomSheet {...props} />;
  }

  return <UseMusicButton.Dropdown {...props} />;
}

UseMusicButton.Dropdown = function UseMusicButtonDropdown({
  isrc,
  title,
  youtubeVideoId,
  thumbnailImageUrl,
  className,
}: UseMusicButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <DropdownTrigger asChild>
        <Button
          variant="ghost"
          className={cn("p-2 hover:bg-backgroundDark rounded-[8px] text-textPrimary", className)}
          onClick={(e) => {
            e.stopPropagation();
            trackNewEvent(NewTrackingEvent.ClickUseMusicButton, {});
            setOpen(true);
          }}
        >
          <Icon name="channel-bold" className="size-5" />
        </Button>
      </DropdownTrigger>
      <DropdownContent className="p-6 max-w-[360px]">
        <Typography size="md" weight={600} color="textPrimary" className="mb-[14px]">
          {isrc}
        </Typography>
        <Typography asChild size="paragraphSm" weight={400} color="textSecondary" className="mb-6">
          <ul className="list-disc pl-[20px] mb-6">
            <li>
              Uploading music from desktop may cause inaccurate view counts. We recommend using mobile for uploads.
            </li>
            <li>This track is available for use in all videos, including monetized ones.</li>
          </ul>
        </Typography>
        <div className="flex gap-3">
          <ISRCCopyButton
            className="flex-1"
            isrc={isrc}
            onClick={() => {
              setOpen(false);
            }}
          >
            Copy Code
          </ISRCCopyButton>
          <QRCodeButton
            className="flex-1"
            thumbnailImageUrl={thumbnailImageUrl}
            isrc={isrc}
            title={title}
            youtubeVideoId={youtubeVideoId}
            onClick={() => {
              setOpen(false);
            }}
          >
            Scan QR
          </QRCodeButton>
        </div>
      </DropdownContent>
    </Dropdown>
  );
};

UseMusicButton.BottomSheet = function UseMusicButtonBottomSheet({
  isrc,
  title,
  youtubeVideoId,
  thumbnailImageUrl,
  className,
}: UseMusicButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger asChild>
        <Button
          variant="ghost"
          className={cn("p-2 rounded-[8px] text-textPrimary", className)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            trackNewEvent(NewTrackingEvent.ClickUseMusicButton, {});
            setOpen(true);
          }}
        >
          <Icon name="channel-bold" className="size-5" />
        </Button>
      </BottomSheetTrigger>
      <BottomSheetContent>
        <BottomSheetHeader className="text-left">
          <BottomSheetTitle className="sr-only">{isrc}</BottomSheetTitle>
          <BottomSheetDescription className="sr-only">{title}</BottomSheetDescription>
          <div className="flex items-center gap-3">
            <Thumbnail variant="square">
              <ThumbnailImage src={thumbnailImageUrl} alt={title} />
              <ThumbnailFallback>{title?.slice(0, 2)}</ThumbnailFallback>
            </Thumbnail>
            <div className="flex flex-col gap-1 flex-1">
              <Typography size="sm" weight={400}>
                {title}
              </Typography>
              <Typography size="xs" weight={400} color="textMuted">
                {isrc}
              </Typography>
            </div>
          </div>
        </BottomSheetHeader>
        <BottomSheetFooter>
          <div className="flex flex-col gap-[10px]">
            <ISRCCopyButton
              className="w-full"
              isrc={isrc}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              Copy Code
            </ISRCCopyButton>
            <UseTrackButton
              className="w-full"
              youtubeVideoId={youtubeVideoId}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              Use this track
            </UseTrackButton>
          </div>
        </BottomSheetFooter>
      </BottomSheetContent>
    </BottomSheet>
  );
};

interface ISRCCopyButtonProps extends HTMLAttributes<HTMLButtonElement> {
  isrc: string;
}

function ISRCCopyButton({ isrc, onClick, ...props }: ISRCCopyButtonProps) {
  const { info, error } = useToastMessage();

  return (
    <Button
      leftAddon={<Icon name="copy" className="w-4 h-4" />}
      size="lg"
      variant="outlined"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
        navigator.clipboard
          .writeText(isrc)
          .then(() => {
            info(`Code copied '${isrc}'`);
          })
          .catch(() => {
            error(`Failed to copy: ${isrc}`);
          });
      }}
      {...props}
    >
      Copy ISRC
    </Button>
  );
}

interface QRCodeButtonProps extends HTMLAttributes<HTMLButtonElement> {
  thumbnailImageUrl: string;
  isrc: string;
  title: string;
  youtubeVideoId: string;
}

function QRCodeButton({ thumbnailImageUrl, isrc, title, youtubeVideoId, onClick, ...props }: QRCodeButtonProps) {
  const { open: openQRCodeModal } = useQRCodeModal({
    thumbnailImageUrl,
    isrc,
    title,
    url: externalUrls.generateYoutubeShortsDeeplink(youtubeVideoId),
  });

  return (
    <Button
      leftAddon={<Icon name="qr-code" className="w-4 h-4" />}
      size="lg"
      variant="black"
      onClick={(e) => {
        e.stopPropagation();
        openQRCodeModal();
        onClick?.(e);
      }}
      {...props}
    />
  );
}

function useQRCodeModal({
  url,
  thumbnailImageUrl,
  title,
  isrc,
}: { url: string; thumbnailImageUrl: string; title: string; isrc: string }) {
  const { open: modalOpen } = useModal();

  const open = () =>
    modalOpen({
      title: "Scan QR Code",
      className: "max-w-[300px]",
      withNoHeader: true,
      children: (
        <div className="flex flex-col items-center gap-4 pt-10">
          <Typography font="unbounded" color="textPrimary" size="lg" weight={400}>
            Scan QR Code
          </Typography>
          <div
            className="size-[200px] p-[40px]"
            style={{
              backgroundImage: "url(/assets/qr-background.svg)",
            }}
            dangerouslySetInnerHTML={{ __html: renderQRAsSVG(url) }}
          />
          <div className="flex items-center gap-3 w-full">
            <Thumbnail variant="square" className="size-10">
              <ThumbnailImage src={thumbnailImageUrl} alt={title} />
              <ThumbnailFallback>{title.slice(0, 2)}</ThumbnailFallback>
            </Thumbnail>
            <div className="flex flex-col gap-1">
              <Typography size="sm" color="textPrimary">
                {title}
              </Typography>
              <Typography size="xs" color="textSecondary">
                {isrc}
              </Typography>
            </div>
          </div>
        </div>
      ),
    });

  return { open };
}

interface UseTrackButtonProps extends HTMLAttributes<HTMLButtonElement> {
  youtubeVideoId: string;
  bold?: boolean;
}

function UseTrackButton({ youtubeVideoId, onClick, ...props }: UseTrackButtonProps) {
  return (
    <Button
      size="lg"
      leftAddon={<Icon name="channel-bold" className="w-4 h-4" />}
      variant="black"
      onClick={(e) => {
        location.href = externalUrls.generateYoutubeShortsDeeplink(youtubeVideoId);
        onClick?.(e);
      }}
      {...props}
    />
  );
}
